import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gem, LogOut, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatINR, CATEGORIES, apiError, slugify } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ProductFormDialog from "@/pages/admin/ProductFormDialog";
import OrdersTab from "@/pages/admin/OrdersTab";
import CouponsTab from "@/pages/admin/CouponsTab";
import BannerTab from "@/pages/admin/BannerTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import PagesTab from "@/pages/admin/PagesTab";

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-cream/10 bg-espresso-surface px-5 py-4">
    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-cream/40">{label}</p>
    <p className="mt-2 font-serif text-3xl text-cream">{value}</p>
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    api
      .get("/admin/products", {
        params: { search: debounced || undefined, category: category === "All" ? undefined : category },
      })
      .then((r) => setProducts(r.data.products))
      .catch((err) => toast.error(apiError(err, "Could not load products")))
      .finally(() => setLoading(false));
  }, [debounced, category]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refresh = () => {
    fetchProducts();
    fetchStats();
  };

  const toggleFlag = async (product, field, value) => {
    const prev = products;
    setProducts((list) =>
      list.map((p) => (p.id === product.id ? { ...p, [field]: value } : p))
    );
    try {
      await api.patch(`/admin/products/${product.id}/flags`, { [field]: value });
      fetchStats();
    } catch (err) {
      setProducts(prev);
      toast.error(apiError(err, "Could not update product"));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/products/${deleting.id}`);
      toast.success(`"${deleting.name}" deleted`);
      setDeleting(null);
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Could not delete product"));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-espresso p-4 text-cream sm:p-6 lg:p-12">
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-gold/20 pb-6">
          <div>
            <p className="font-accent text-xl font-bold tracking-[0.35em] text-cream">AARMIRA</p>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-gold-muted">
              Atelier Admin — {user?.email}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-store-link"
              className="border border-cream/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/70 transition-colors hover:border-gold hover:text-gold"
            >
              View Store
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-cream/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/70 transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </header>

        <div className="mt-8 flex gap-2">
          <button
            data-testid="admin-tab-products"
            onClick={() => setTab("products")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              tab === "products"
                ? "border-gold bg-gold text-espresso"
                : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
            }`}
          >
            Products
          </button>
          <button
            data-testid="admin-tab-orders"
            onClick={() => setTab("orders")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              tab === "orders"
                ? "border-gold bg-gold text-espresso"
                : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
            }`}
          >
            Orders
          </button>
          <button
            data-testid="admin-tab-banner"
            onClick={() => setTab("banner")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              tab === "banner"
                ? "border-gold bg-gold text-espresso"
                : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
            }`}
          >
            Banner
          </button>
          <button
            data-testid="admin-tab-coupons"
            onClick={() => setTab("coupons")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              tab === "coupons"
                ? "border-gold bg-gold text-espresso"
                : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
            }`}
          >
            Coupons
          </button>
          <button
            data-testid="admin-tab-pages"
            onClick={() => setTab("pages")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              tab === "pages"
                ? "border-gold bg-gold text-espresso"
                : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
            }`}
          >
            Pages
          </button>
          <button
            data-testid="admin-tab-settings"
            onClick={() => setTab("settings")}
            className={`border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-all duration-300 ${
              tab === "settings"
                ? "border-gold bg-gold text-espresso"
                : "border-cream/15 text-cream/60 hover:border-gold hover:text-gold"
            }`}
          >
            Settings
          </button>
        </div>

        {tab === "orders" && <OrdersTab />}

        {tab === "banner" && <BannerTab />}

        {tab === "coupons" && <CouponsTab />}

        {tab === "pages" && <PagesTab />}

        {tab === "settings" && <SettingsTab />}

        {tab === "products" && (<>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard label="Total Products" value={stats?.total ?? "—"} testid="admin-stat-total" />
          <StatCard label="Active" value={stats?.active ?? "—"} testid="admin-stat-active" />
          <StatCard label="Hidden" value={stats?.hidden ?? "—"} testid="admin-stat-hidden" />
          <StatCard label="Featured" value={stats?.featured ?? "—"} testid="admin-stat-featured" />
          <StatCard label="Out of Stock" value={stats?.out_of_stock ?? "—"} testid="admin-stat-out-of-stock" />
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cream/30" />
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or category…"
              className="w-full border border-cream/15 bg-transparent py-2.5 pl-9 pr-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              data-testid="admin-category-filter"
              className="w-full rounded-none border-cream/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.15em] text-cream sm:w-56"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-gold/30 bg-espresso-surface text-cream">
              <SelectItem value="All">All Categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`admin-filter-${slugify(c)}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            data-testid="admin-add-product-button"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 bg-gold px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98]"
          >
            <Plus size={14} /> Add Product
          </button>
        </div>

        <div className="mt-6 overflow-x-auto border border-cream/10" data-testid="admin-product-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-gold/20 bg-espresso-surface font-mono text-[9px] uppercase tracking-[0.2em] text-cream/40">
                <th className="px-4 py-3.5 font-medium">Image</th>
                <th className="px-4 py-3.5 font-medium">Name</th>
                <th className="px-4 py-3.5 font-medium">Category</th>
                <th className="px-4 py-3.5 font-medium">Price</th>
                <th className="px-4 py-3.5 font-medium">Stock</th>
                <th className="px-4 py-3.5 font-medium">Featured</th>
                <th className="px-4 py-3.5 font-medium">New</th>
                <th className="px-4 py-3.5 font-medium">Active</th>
                <th className="px-4 py-3.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream/8">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-14 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30">
                    Opening the vault…
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-14 text-center" data-testid="admin-empty-state">
                    <Gem size={28} strokeWidth={1} className="mx-auto text-gold-muted/50" />
                    <p className="mt-4 font-serif text-xl text-cream/80">
                      {debounced ? "No products match your search" : "No products yet"}
                    </p>
                    <p className="mt-2 text-xs text-cream/40">
                      {debounced
                        ? "Try a different name or category."
                        : "Add your first treasure with the button above."}
                    </p>
                  </td>
                </tr>
              ) : (
                products.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`admin-product-row-${p.id}`}
                    className={`transition-colors hover:bg-cream/[0.03] ${!p.active ? "opacity-50" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <div className="h-12 w-12 overflow-hidden bg-espresso-surface">
                        {p.images?.[0] ? (
                          <img src={p.images[0]} alt={p.name} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Gem size={16} strokeWidth={1} className="text-gold-muted/40" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="max-w-[220px] px-4 py-3">
                      <p className="truncate font-serif text-base text-cream">{p.name}</p>
                      {p.sku && <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-cream/30">{p.sku}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-cream/60">
                      {p.category}
                      {p.subcategory && (
                        <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-gold-muted">
                          {p.subcategory}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-cream">{formatINR(p.price)}</p>
                      {p.original_price > p.price && (
                        <p className="text-xs text-cream/30 line-through">{formatINR(p.original_price)}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          data-testid={`admin-stock-toggle-${p.id}`}
                          checked={p.in_stock}
                          onCheckedChange={(v) => toggleFlag(p, "in_stock", v)}
                        />
                        <span
                          className={`font-mono text-[9px] uppercase tracking-[0.15em] ${
                            p.in_stock ? "text-gold" : "text-red-400"
                          }`}
                        >
                          {p.in_stock ? "In stock" : "Out"}
                        </span>
                      </div>
                      {p.stock_quantity != null && (
                        <p className="mt-1 font-mono text-[9px] text-cream/30">Qty: {p.stock_quantity}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        data-testid={`admin-featured-toggle-${p.id}`}
                        checked={p.featured}
                        onCheckedChange={(v) => toggleFlag(p, "featured", v)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        data-testid={`admin-new-arrival-toggle-${p.id}`}
                        checked={p.new_arrival}
                        onCheckedChange={(v) => toggleFlag(p, "new_arrival", v)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Switch
                        data-testid={`admin-active-toggle-${p.id}`}
                        checked={p.active}
                        onCheckedChange={(v) => toggleFlag(p, "active", v)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          data-testid={`admin-edit-button-${p.id}`}
                          onClick={() => {
                            setEditing(p);
                            setFormOpen(true);
                          }}
                          className="flex h-8 w-8 items-center justify-center border border-cream/15 text-cream/60 transition-colors hover:border-gold hover:text-gold"
                          aria-label={`Edit ${p.name}`}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          data-testid={`admin-delete-button-${p.id}`}
                          onClick={() => setDeleting(p)}
                          className="flex h-8 w-8 items-center justify-center border border-cream/15 text-cream/60 transition-colors hover:border-red-400 hover:text-red-400"
                          aria-label={`Delete ${p.name}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </>)}
      </div>

      <ProductFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        product={editing}
        onSaved={refresh}
      />

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent className="rounded-none border-gold/30 bg-espresso-surface text-cream" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-cream">
              Delete this treasure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-cream/50">
              "{deleting?.name}" will be permanently removed from the catalogue. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-delete-cancel-button"
              className="rounded-none border-cream/20 bg-transparent font-mono text-[10px] uppercase tracking-[0.2em] text-cream/70 hover:bg-cream/5 hover:text-cream"
            >
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm-button"
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="rounded-none bg-red-800 font-mono text-[10px] uppercase tracking-[0.2em] text-cream hover:bg-red-700"
            >
              {deleteBusy ? "Deleting…" : "Delete forever"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default AdminDashboard;
